import { FloatingNavigationOpenMenuButton } from "@src/appV2/redesign/components/FloatingNavigation/OpenMenuButton";
import {
  FloatingNavigationTab,
  FloatingNavigationTabs,
} from "@src/appV2/redesign/components/FloatingNavigation/Tabs";
import { CHAT_PAGE_PATH } from "@src/appV2/redesign/Chat/page/paths";
import { useEffect, useState } from "react";
import { matchPath, useLocation } from "react-router-dom";
import { useFloatingTabs } from "../../../../appV2/redesign/AppLayout/Mobile/FloatingTabsContext";
import { AppLayoutPageWrapperMobile } from "@src/appV2/redesign/AppLayout/Mobile/PageWrapperMobile";
import { routes } from "@src/appV2/redesign/utils/routes";
import { AppLayoutWrapperMobile } from "./Wrapper";
import { NavigationMenuDrawerMobile } from "./Navigation/NavigationMenuDrawer";
import { NavigationMenuHeaderMobile } from "./Navigation/NavigationMenuHeader";
import { NavigationMenuFooterMobile } from "./Navigation/NavigationMenuFooter";
import { NavigationMenuItemsMobile } from "./Navigation/NavigationMenuItems";
import { NavigationMenuItemsContainerMobile } from "@src/appV2/redesign/AppLayout/Mobile/Navigation/NavigationMenuItemsContainer";
import { NavigationMenuSectionTitleDesktop } from "@src/appV2/redesign/AppLayout/Desktop/Navigation/MenuSectionTitle";
import { NavigationMenuItemDesktop } from "@src/appV2/redesign/AppLayout/Desktop/Navigation/MenuItem";
import { CurrentlyViewedSectionDesktop } from "@src/appV2/redesign/AppLayout/Desktop/Navigation/CurrentlyViewedSection";
import { Box } from "@mui/material";
import { Platform } from "@src/mobile/utils/platform";
import { useThemeMode } from "@src/appV2/UserPreferences/UserPreferencesProvider";
import { AllRoutes } from "../Shared/Navigation/routes/AllRoutes";
import { RecentlyViewedSectionDesktop } from "../Desktop/Navigation/RecentlyViewedSection";

// Public auth surfaces that must render without app navigation, even when the
// user happens to be authenticated (e.g. tapping a reset-password link while
// already signed in). NavigationMenuContainer already bypasses this layout
// for unauthenticated visits to these paths via isPreAuthRoute.
const AUTH_ONLY_PATHS = [
  routes.login,
  routes.loginVerify,
  routes.v2LoginWithEmailPassword,
  routes.v2ResetPassword,
  "/v2/email-login-link",
];

export interface AppLayoutMobileProps {
  admin: boolean;
  isAdmin: boolean;
  isWorker: boolean;
  isWorkplace: boolean;
  userName: string;
  userId: string;
  logoutConfirm: () => void;
  renderStartPage: () => JSX.Element | null;
}

function Divider() {
  return (
    <Box
      sx={(theme) => ({
        height: "1px",
        backgroundColor: theme.palette.navbar?.sectionDivider,
      })}
    />
  );
}

export function AppLayoutMobile(props: AppLayoutMobileProps) {
  const {
    admin,
    isAdmin,
    isWorker,
    isWorkplace,
    userName,
    userId,
    logoutConfirm,
    renderStartPage,
  } = props;

  const [showSidenav, setShowSidenav] = useState(false);
  const { tabs, showTabs, activeTab, setActiveTab } = useFloatingTabs();
  const { themeMode } = useThemeMode();
  const location = useLocation();
  // Only the conversation view (/chat/:workplaceId/...) has a bottom-anchored
  // chat input that the floating menu button overlaps. The bare channel list
  // at /chat has no such input, so it should keep the floating nav like every
  // other page.
  const isChatConversationPage = location.pathname.startsWith(`${CHAT_PAGE_PATH}/`);
  const isAuthOnlyRoute = AUTH_ONLY_PATHS.some((path) =>
    matchPath(location.pathname, { path, exact: true })
  );

  useEffect(() => {
    if (!Platform.isMobile) {
      return;
    }

    // Capacitor Style.Dark = light content (for a dark background),
    // Style.Light = dark content (for a light background).
    async function updateStatusBar() {
      const { StatusBar, Style } = await import("@capacitor/status-bar");
      await StatusBar.setStyle({ style: themeMode === "dark" ? Style.Dark : Style.Light });
    }

    void updateStatusBar();
  }, [themeMode]);

  // Reset to the default dark-font status bar when leaving the mobile app
  // layout, so any non-mobile-layout surface (e.g. auth flows rendered
  // outside this tree) isn't left with whatever style we last set here.
  useEffect(() => {
    if (!Platform.isMobile) {
      return undefined;
    }
    return () => {
      void import("@capacitor/status-bar").then(({ StatusBar, Style }) => {
        void StatusBar.setStyle({ style: Style.Light });
      });
    };
  }, []);

  return (
    <AppLayoutWrapperMobile>
      <NavigationMenuDrawerMobile
        open={showSidenav}
        onClose={() => {
          setShowSidenav(false);
        }}
      >
        <NavigationMenuHeaderMobile onClose={() => setShowSidenav(false)} />

        <Divider />

        <NavigationMenuItemsContainerMobile>
          <NavigationMenuItemsMobile
            isAdmin={isAdmin}
            isWorker={isWorker}
            isWorkplace={isWorkplace}
            closeSidenav={() => setShowSidenav(false)}
          />
          <CurrentlyViewedSectionDesktop
            admin={admin}
            isWorker={isWorker}
            isWorkplace={isWorkplace}
            currentName={userName}
            currentUserId={userId}
          />
          <RecentlyViewedSectionDesktop admin={admin} />
          <NavigationMenuSectionTitleDesktop>Logout</NavigationMenuSectionTitleDesktop>
          <NavigationMenuItemDesktop
            iconType="door-straight"
            isSelected={false}
            onClick={() => {
              setShowSidenav(false);
              logoutConfirm();
            }}
          >
            Logout
          </NavigationMenuItemDesktop>
        </NavigationMenuItemsContainerMobile>

        <Divider />

        <NavigationMenuFooterMobile closeSidenav={() => setShowSidenav(false)} />
      </NavigationMenuDrawerMobile>

      <AppLayoutPageWrapperMobile>
        <AllRoutes
          admin={admin}
          isAdmin={isAdmin}
          isWorker={isWorker}
          isWorkplace={isWorkplace}
          renderStartPage={renderStartPage}
        />
      </AppLayoutPageWrapperMobile>

      {!isAuthOnlyRoute && !isChatConversationPage && tabs.length > 0 && showTabs && (
        <FloatingNavigationTabs
          value={activeTab}
          onChange={(_, newValue) => {
            setActiveTab(newValue as string);
            const tab = tabs.find((t) => t.value === newValue);
            if (tab?.onClick) {
              tab.onClick();
            }
          }}
        >
          {tabs.map((tab) => (
            <FloatingNavigationTab
              key={tab.value}
              iconType={tab.iconType}
              label={tab.label}
              value={tab.value}
            />
          ))}
        </FloatingNavigationTabs>
      )}

      {!isAuthOnlyRoute && !isChatConversationPage && (
        <FloatingNavigationOpenMenuButton
          onClick={() => {
            setShowSidenav(true);
          }}
        />
      )}
    </AppLayoutWrapperMobile>
  );
}
